import React, { useEffect, useState } from 'react';
import { Copy, Loader2, Save, TestTube2 } from 'lucide-react';
import client from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { CALLBACK_PATH, PAYLUX_DEFAULTS, changedFields } from '../lib/paylux';

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object') return detail.message || fallback;
  return detail || fallback;
};

/**
 * Ayarlar → Finans → Paylux.
 *
 * Bölüm kendi state'ini ve Kaydet düğmesini taşır: ayarlar
 * `provider_configs` satırındadır ve `PATCH /admin/settings` gövdesine hiç
 * girmez. `onEnabledChange` finans bölüm seçicisindeki aktiflik noktasını
 * besler.
 */
const PayluxSettings = ({ paymentBase = '', siteBase = '', onEnabledChange }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(PAYLUX_DEFAULTS);
  const [initial, setInitial] = useState(PAYLUX_DEFAULTS);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  // Ayarlar okunamadıysa form varsayılanları çizer; kaydetmek onları canlının
  // üzerine yazmak olurdu, bu yüzden bölüm okunana kadar salt okunurdur.
  const [loadError, setLoadError] = useState('');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const callbackBase = paymentBase || origin;
  const returnBase = siteBase || origin;
  const callbackUrl = `${callbackBase}${CALLBACK_PATH}`;

  const apply = (data) => {
    const merged = {
      ...PAYLUX_DEFAULTS, ...data, api_key: '', secret_key: '',
    };
    setForm(merged);
    setInitial(merged);
    setMeta(data || {});
    onEnabledChange?.(Boolean(data?.enabled));
  };

  useEffect(() => {
    let alive = true;
    client.get('/admin/paylux/config')
      .then(({ data }) => {
        if (!alive) return;
        apply(data);
        setLoadError('');
      })
      .catch((error) => {
        if (!alive) return;
        const message = errorText(error, 'Bilinmeyen hata');
        setLoadError(message);
        toast({
          title: 'Paylux ayarları okunamadı',
          description: message,
          variant: 'destructive',
        });
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const pending = changedFields(form, initial, {
    paymentBase: callbackBase, siteBase: returnBase,
  });
  const dirty = Object.keys(pending).length > 0;

  const save = async () => {
    if (loadError || !dirty) return;
    setBusy('save');
    try {
      const { data } = await client.patch('/admin/paylux/config', pending);
      apply(data);
      toast({ title: 'Paylux ayarları kaydedildi' });
    } catch (error) {
      toast({
        title: 'Paylux kaydedilemedi',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const test = async () => {
    setBusy('test');
    try {
      const { data } = await client.post('/admin/paylux/test');
      toast({ title: data?.message || 'Paylux ayarları geçerli' });
    } catch (error) {
      toast({
        title: 'Paylux doğrulaması başarısız',
        description: errorText(error, 'Bilinmeyen hata'),
        variant: 'destructive',
      });
    } finally {
      setBusy('');
    }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text)
      .then(() => toast({ title: 'Kopyalandı' }))
      .catch(() => {});
  };

  if (loading) {
    return (
      <div className="col-span-full flex items-center gap-2 text-sm text-slate-400">
        <Loader2 size={14} className="animate-spin" /> Paylux ayarları yükleniyor…
      </div>
    );
  }

  return (
    <>
      <div className="col-span-full mt-1">
        <h3 className="text-sm font-bold text-white">Paylux</h3>
        <p className="mt-1 text-xs text-slate-400">
          Havale/EFT yatırım ve çekim. Yatırımda sağlayıcı işleme bir IBAN ve
          <strong> ödenecek tutarı </strong> atar — oyuncu ekranda o tutarı
          görür ve bakiyeye giren de sağlayıcının onayladığı tutardır. Çekim
          önce <strong>yönetici onayından</strong> geçer, sonra sağlayıcıya
          iletilir. Ayarlar kaydedilince oyuncunun yöntem listesindeki
          {' '}<strong>Paylux Havale / EFT</strong> satırı da güncellenir.
        </p>
      </div>

      {loadError && (
        <div className="col-span-full rounded-lg border border-rose-500/40 p-3 text-xs text-rose-200">
          Ayarlar okunamadı ({loadError}); bölüm salt okunur.
        </div>
      )}

      <div className="col-span-full provider-enable">
        <label className="flex items-center gap-2 text-sm font-semibold text-white">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
          />
          Paylux entegrasyonu aktif
        </label>
        <p className="mt-1 text-[11px] text-slate-400">
          Açmak için API anahtarı, firma kimliği, secret key ve bildirim adresi
          onayı gerekir.
        </p>
      </div>

      <label className="settings-inline-field">
        API adresi
        <input
          value={form.api_url || ''}
          onChange={(e) => set('api_url', e.target.value)}
          className="admin-input mt-1"
        />
      </label>
      <label className="settings-inline-field">
        Firma ID
        <input
          value={form.firma_id || ''}
          onChange={(e) => set('firma_id', e.target.value)}
          className="admin-input mt-1"
          placeholder="Örn. 1205"
        />
        <span className="mt-1 block text-[11px] text-slate-400">
          Bildirim hash'inin ilk parçasıdır. Yanlışsa istekler çalışır ama
          gelen hiçbir bildirim doğrulanamaz.
        </span>
      </label>
      <label className="settings-inline-field">
        API anahtarı (Authorization)
        <input
          type="password"
          autoComplete="new-password"
          placeholder={meta.api_key_configured ? 'Kayıtlı — değiştirmek için yazın' : 'paylux_…'}
          value={form.api_key}
          onChange={(e) => set('api_key', e.target.value)}
          className="admin-input mt-1"
        />
      </label>
      <label className="settings-inline-field">
        Secret key
        <input
          type="password"
          autoComplete="new-password"
          placeholder={meta.secret_key_configured ? 'Kayıtlı — değiştirmek için yazın' : 'Firma secret key'}
          value={form.secret_key}
          onChange={(e) => set('secret_key', e.target.value)}
          className="admin-input mt-1"
        />
        <span className="mt-1 block text-[11px] text-slate-400">
          Hem isteğin gövdesine yazılır hem bildirim hash'ine girer.
        </span>
      </label>

      <div className="col-span-full rounded-lg border border-slate-700/60 p-3">
        <div className="text-xs text-slate-300">
          Bildirim adresi (yatırım ve çekim için aynı)
        </div>
        <div className="mt-1 flex items-center gap-2">
          <code className="flex-1 break-all rounded bg-slate-900/60 px-2 py-1.5 text-xs text-slate-200">
            {callbackUrl}
          </code>
          <button type="button" onClick={() => copy(callbackUrl)} className="admin-btn-secondary p-2" aria-label="Kopyala">
            <Copy size={13} />
          </button>
        </div>
        <p className="mt-2 text-[11px] leading-4 text-slate-400">
          Adres <strong>istekte gönderilmez</strong>; yalnızca Paylux
          panelinde tanımlıdır. API adresi, anahtar, firma kimliği ya da bu
          adres değiştiğinde onay kendiliğinden kalkar ve entegrasyon
          kaydedilemez.
        </p>
        <label className="mt-2 flex items-center gap-2 text-xs text-white">
          <input
            type="checkbox"
            checked={!!form.callback_registered}
            onChange={(e) => set('callback_registered', e.target.checked)}
          />
          Bildirim adresini Paylux panelinde tanımladım
        </label>
      </div>

      {[
        { key: 'deposit', title: 'yatırım', cls: 'is-deposit' },
        { key: 'withdraw', title: 'çekim', cls: 'is-withdraw' },
      ].map((side) => (
        <section key={side.key} className={`provider-money ${side.cls}`}>
          <label className="flex items-center gap-2 text-sm font-bold text-white">
            <input
              type="checkbox"
              checked={form[`${side.key}_enabled`] !== false}
              onChange={(e) => set(`${side.key}_enabled`, e.target.checked)}
            />
            Paylux {side.title} açık
          </label>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {['min', 'max'].map((bound) => (
              <label key={bound} className="settings-inline-field">
                {bound === 'min' ? 'Minimum' : 'Maksimum'} {side.title} (₺)
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={form[`${side.key}_${bound}_amount`] ?? ''}
                  onChange={(e) => set(`${side.key}_${bound}_amount`, Number(e.target.value))}
                  className="admin-input mt-1"
                />
              </label>
            ))}
          </div>
        </section>
      ))}

      <div className="col-span-full flex flex-wrap gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!!busy || !dirty || !!loadError}
          className="admin-btn-primary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'save' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Paylux Ayarlarını Kaydet
        </button>
        <button
          type="button"
          onClick={test}
          disabled={!!busy || !meta.configured}
          className="admin-btn-secondary inline-flex items-center gap-2 px-4 py-2.5"
        >
          {busy === 'test' ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
          Ayarları Doğrula
        </button>
      </div>
      <p className="col-span-full text-[11px] leading-4 text-slate-400">
        Paylux'te işlem üretmeyen bir bağlantı testi ucu yoktur (durum sorgusu
        bir işlem numarası ister); düğme yalnızca kurulumun eksiksiz olduğunu
        doğrular. Gerçek bağlantı ilk yatırımda sınanır.
      </p>
    </>
  );
};

export default PayluxSettings;
